import classNames from 'classnames'
import { useTranslation } from 'next-i18next'

import { ExamStickerPrices } from '~/shared/hooks/useExamCampaign'

interface StickerSize {
  card: string
  top: string
  main: string
  before: string
  suffix: string
  pill: string
}

const SIZES: Record<'large' | 'small', StickerSize> = {
  large: {
    card: 'rounded-28 pb-7.5 pl-7 pr-8 pt-4.5',
    top: 'text-8.5 leading-8.5',
    main: 'text-21.5 leading-none',
    before: 'text-9',
    suffix: 'text-8',
    pill: '-bottom-5 px-4.5 py-2 text-4.5',
  },
  small: {
    card: 'rounded-18 pb-5 pl-4.5 pr-5.5 pt-3.25',
    top: 'text-6 leading-6',
    main: 'text-15 leading-none',
    before: 'text-6',
    suffix: 'text-5.75',
    pill: '-bottom-3.5 px-3.25 py-1.5 text-3.25',
  },
}

interface Props {
  prices: ExamStickerPrices
  className?: string
}

export default function ExamCampaignSticker({ prices, className = '' }: Props): React.ReactElement {
  const { t } = useTranslation('landing', { keyPrefix: 'intro.examSticker' })
  const size = SIZES[prices.compact ? 'small' : 'large']

  return (
    <div className={classNames('rotate-9', prices.compact && '-translate-y-3', className)}>
      <div className="relative inline-block select-none">
        <div aria-hidden className="absolute -inset-2.5 rounded-32 border-2 border-dashed border-white bg-purple/20" />
        <div
          className={classNames(
            'relative flex flex-col items-center whitespace-nowrap bg-gradient-v4-cta-band font-extrabold shadow-cta-drop text-shadow-md',
            size.card,
          )}
        >
          <p className={classNames('text-white', size.top)}>{t('top')}</p>
          <p className={classNames('text-green-lime', size.main)}>
            <span className={classNames('text-green-lime/60 line-through', size.before)}>{prices.before}</span>{' '}
            {prices.price}
            <span className={size.suffix}>{t('perMonth')}</span>
          </p>
        </div>
        <p
          className={classNames(
            'absolute left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-lime-200 font-bold text-black-rich shadow-cta-drop',
            size.pill,
          )}
        >
          {t('pill')}
        </p>
      </div>
    </div>
  )
}
